import { baseAPI } from '~shared/api/base-api';

import type {
  ApprovePaymentRunLockParams,
  ApprovePaymentRunLockResponse,
  GetPaymentRunReviewParams,
  PaymentRunReviewDetail,
  PaymentRunReviewResponse,
  PostponePaymentRunResponse,
  RestorePaymentRunReviewParams,
  RestorePaymentRunReviewResponse,
  UnlockPaymentRunResponse,
} from './types';

export function paymentRunReviewSearchParam(
  search: string | undefined,
): string | undefined {
  const trimmed = search?.trim();
  return trimmed !== undefined && trimmed !== '' ? trimmed : undefined;
}

export const getPaymentRunReviewReportAPI = async (
  params: GetPaymentRunReviewParams,
): Promise<PaymentRunReviewResponse> => {
  const searchParam = paymentRunReviewSearchParam(params.search);
  const { data } = await baseAPI.get<PaymentRunReviewResponse>(
    '/reporting/payment-run-review',
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        search: searchParam,
        page: params.page ?? 1,
        limit: params.limit ?? 25,
      },
    },
  );
  return data;
};

export const downloadPaymentRunReviewCSV = async (
  params: GetPaymentRunReviewParams,
): Promise<Blob> => {
  const searchParam = paymentRunReviewSearchParam(params.search);
  const { data } = await baseAPI.get<string>(
    '/reporting/payment-run-review',
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        search: searchParam,
        format: 'csv',
      },
      responseType: 'text',
    },
  );
  return new Blob([data], { type: 'text/csv' });
};

export const approvePaymentRunLockAPI = async (
  params: ApprovePaymentRunLockParams,
): Promise<ApprovePaymentRunLockResponse> => {
  const { data } = await baseAPI.post<ApprovePaymentRunLockResponse>(
    '/reporting/payment-run-review/approve-lock',
    {
      startDate: params.startDate,
      endDate: params.endDate,
      flaggedItemsAcknowledged: params.flaggedItemsAcknowledged,
      ...(params.invoiceIds && params.invoiceIds.length > 0
        ? { invoiceIds: params.invoiceIds }
        : {}),
    },
  );
  return data;
};

export const unlockPaymentRunAPI = async (): Promise<UnlockPaymentRunResponse> => {
  const { data } = await baseAPI.post<UnlockPaymentRunResponse>(
    '/reporting/payment-run-review/unlock',
    {},
  );
  return data;
};

export const postponePaymentRunAPI = async (params?: {
  hours?: number;
}): Promise<PostponePaymentRunResponse> => {
  const { data } = await baseAPI.post<PostponePaymentRunResponse>(
    '/reporting/payment-run-review/postpone',
    { hours: params?.hours ?? 24 },
  );
  return data;
};

export const getPaymentRunReviewDetailAPI = async (params: {
  invoiceId: string;
  startDate: string;
  endDate: string;
}): Promise<PaymentRunReviewDetail> => {
  const { data } = await baseAPI.get<PaymentRunReviewDetail>(
    `/reporting/payment-run-review/detail/${params.invoiceId}`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
      },
    },
  );
  return data;
};

export const restorePaymentRunReviewAPI = async (
  params: RestorePaymentRunReviewParams,
): Promise<RestorePaymentRunReviewResponse> => {
  const { data } = await baseAPI.post<RestorePaymentRunReviewResponse>(
    '/reporting/payment-run-review/restore',
    {
      invoiceId: params.invoiceId,
      reasonCode: params.reasonCode,
      startDate: params.startDate,
      endDate: params.endDate,
    },
  );
  return data;
};
